import { useCallback, useEffect, useState } from 'react'
import {
  fetchOperations, formatCount, formatCredits, formatMinutes, formatTime, nameIn,
  pluralise, translateError,
  type CoverageStanding, type NotReadyCount, type OperationsView,
  type SupervisorLane,
} from '@hk/core'
import {
  Card, CardBody, CardHeader, ErrorState, LoadingState, Progress, Stat, StatStrip,
  useLocale,
} from '@hk/ui'
import { STRINGS } from '../i18n/strings'
import { EscalationCard } from './Escalations'

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/* ============================================================================
   W-10 — THE OPERATIONS DASHBOARD.

   Pack 1, unusually directly: *"The landing screen for a housekeeping manager. Its job is
   not to show everything — it is to answer one question in the first five seconds: can we
   cover today?"* And then how: *"the headline is a gap, not a total."*

   ── THIS FILE DECIDES NOTHING ────────────────────────────────────────────────
   Every figure on it arrives computed. Readiness came from `RoomCondition`, the lanes from
   the same builder the assignment board and the handset use, the four decision cards from
   the same query W-61 reads — and the cards are literally W-61's component, imported.

   **THAT IS THE WHOLE DESIGN.** A dashboard is the one screen on which a manager would
   believe the wrong number, so it is built so that it CANNOT hold a second opinion: there
   is no arithmetic here to disagree with, and the one component that could have been
   copied is not.

   ── THE HEADLINE IS A SENTENCE, AND THERE ARE FOUR ───────────────────────────
   Not a figure with a colour. "Covered" and "short by four" and "short by forty and it
   cannot be found" are three different mornings with three different next actions, and the
   last is one a manager settles with the front desk rather than on this screen. A single
   number would have forced this screen to invent the threshold between them.
   ========================================================================= */

export function Operations({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [view, setView] = useState<OperationsView>()
  const [error, setError] = useState<unknown>()
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setView(await fetchOperations(propertyId))
      setError(undefined)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [propertyId])

  useEffect(() => { void load() }, [load])

  if (loading) return <LoadingState />

  if (!view) {
    return (
      <ErrorState
        body={translateError(error, locale).message}
        onRetry={() => void load()}
      />
    )
  }

  return (
    <div className="space-y-4 p-4">
      <Headline view={view} />

      <StatStrip>
        <Stat label={t.opsRooms} value={formatCount(view.totalRooms, locale)} />
        <Stat
          label={t.opsDepartures}
          value={formatCount(view.departuresDone, locale)}
          /* **THE NOTE DOES NOT REPEAT THE FIGURE ABOVE IT.** It read "0 of 4 gone"
             under a tile already reading 0, which is the same number twice and a line of
             noise on a screen whose whole claim is that it says one thing quickly. */
          note={fill(t.opsDeparturesNote, {
            expected: formatCount(view.departuresExpected, locale),
          })}
        />
        <Stat label={t.opsOpenTasks} value={formatCount(view.openTasks, locale)} />
        <Stat label={t.opsRunning} value={formatCount(view.runningTasks, locale)} />
        {/* **"TO INSPECT", NOT "OVERDUE" AND NOT "WAITING FOR INSPECTION" (#163).** The
            pack drew an overdue tile; a task here has no due time, so nothing can be late
            against one. What this counts is a queue a person caused — rooms handed in that
            nobody has looked at — which is M-12's tile exactly, down to the field name.

            **AND IT IS NOT THE PANEL'S ROW.** "Waiting for inspection" below counts every
            clean room in a hotel that inspects, which is a larger set; the first render of
            this screen put the two counts under one label three inches apart. The note says
            how long the queue has stood — **null is not zero**. */}
        <Stat
          label={t.opsToInspect}
          value={formatCount(view.awaitingInspection, locale)}
          tone={view.awaitingInspection > 0 ? 'var(--hk-sv-ooo-text)' : undefined}
          note={
            view.oldestWaitingMinutes === null
              ? t.opsWaitingNone
              : fill(t.opsWaitingOldest, {
                  minutes: formatMinutes(view.oldestWaitingMinutes, locale),
                })
          }
        />
        <Stat
          label={t.opsFaults}
          value={formatCount(view.openFaults, locale)}
          note={view.blockingFaults > 0
            ? `${t.opsBlocking} ${formatCount(view.blockingFaults, locale)}`
            : undefined}
          tone={view.blockingFaults > 0 ? 'var(--hk-sv-ooo-text)' : undefined}
        />
      </StatStrip>

      <div className="grid gap-4 lg:grid-cols-2">
        <NotReadyPanel rows={view.notReady} />
        <FloorsPanel lanes={view.lanes} />
      </div>

      {/* **W-61'S OWN CARDS, NOT A SUMMARY OF THEM.** Drawn only when there are any: an
          empty "needs a decision" heading on a landing screen is a panel that trains
          people to scroll past the place the urgent things appear. */}
      {view.decisions.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold">{t.opsDecisions}</h2>
          {view.decisions.map((card, i) => (
            <EscalationCard
              key={`${card.code}:${card.roomNumber ?? ''}:${card.subjectName ?? ''}:${i}`}
              card={card}
            />
          ))}
        </section>
      )}

      <p className="text-xs text-muted-foreground">
        {fill(t.opsAsAt, {
          time: formatTime(new Date(view.generatedAtUtc), locale),
        })}
      </p>
    </div>
  )
}

/**
 * **THE SENTENCE, AND THE FOUR IT CAN BE.**
 *
 * The standing comes from `Coverage` in the domain — a rule with its own tests and its own
 * name for each answer. This function chooses words for it and nothing else; the day the
 * client wants the threshold moved, it moves in one place and this file does not change.
 */
function Headline({ view }: { view: OperationsView }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const tone: Record<CoverageStanding, string> = {
    NO_ARRIVALS: 'var(--hk-muted-fg)',
    COVERED: 'var(--hk-hk-ready-text)',
    SHORTFALL: 'var(--hk-hk-dirty-text)',
    CANNOT_BE_COVERED: 'var(--hk-sv-ooo-text)',
  }

  const title =
    view.standing === 'NO_ARRIVALS' ? t.opsNoArrivals
    : view.standing === 'COVERED' ? t.opsCovered
    : view.standing === 'SHORTFALL'
      ? pluralise(t.opsShort, view.shortfall, locale, formatCount(view.shortfall, locale))
      : pluralise(t.opsCannot, view.shortfall, locale, formatCount(view.shortfall, locale))

  const body =
    view.standing === 'NO_ARRIVALS' ? t.opsNoArrivalsBody
    : view.standing === 'COVERED' ? t.opsCoveredBody
    : view.standing === 'CANNOT_BE_COVERED' ? t.opsCannotBody
    /* **THE DEADLINE IS AN ARRIVAL OR IT IS NOTHING.** Where the property system gave no
       time, the sentence says so rather than filling in a check-in hour this product does
       not hold. */
    : view.nextArrivalUtc
      ? fill(t.opsShortBy, {
          time: formatTime(new Date(view.nextArrivalUtc), locale),
        })
      : t.opsShortNoTime

  return (
    <Card>
      <CardBody className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t.opsQuestion}
        </p>

        {/* **THE TWO FIGURES THE PACK ASKS FOR, SIDE BY SIDE AND SPELLED OUT.** "46
            arrivals, 3 rooms ready" — stated, not left to be inferred from six tiles. */}
        <p className="text-sm text-muted-foreground">
          {pluralise(t.opsArrivals, view.arrivals, locale,
            formatCount(view.arrivals, locale))}
          {' · '}
          {pluralise(t.opsReady, view.readyNow, locale, formatCount(view.readyNow, locale))}
        </p>

        <h1
          className="text-[26px] font-bold leading-tight"
          style={{ color: tone[view.standing] }}
        >
          {title}
        </h1>

        <p className="text-sm text-muted-foreground">{body}</p>

        {/* **WHAT THE FLOORS CAN STILL DO**, and only where it is the question. On a
            covered morning it is noise, and on a morning that cannot be covered it is the
            consolation that hides the problem. */}
        {(view.standing === 'SHORTFALL' || view.standing === 'CANNOT_BE_COVERED') && (
          <p className="text-xs text-muted-foreground">
            {pluralise(t.opsRecoverable, view.recoverable, locale,
              formatCount(view.recoverable, locale))}
          </p>
        )}
      </CardBody>
    </Card>
  )
}

/**
 * **NOT READY, BY REASON** — the pack's own panel, with the pack's own justification:
 * *"the fix differs: unstarted rooms need people, rooms awaiting inspection need a
 * supervisor, and rooms out of service need maintenance."*
 *
 * The words are the room board's. A room reading "Out of order" on one screen and
 * "Unavailable" on the other is two products.
 */
function NotReadyPanel({ rows }: { rows: NotReadyCount[] }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const total = rows.reduce((sum, r) => sum + r.rooms, 0)

  return (
    <Card>
      <CardHeader title={t.opsNotReady} />
      <CardBody className="space-y-3">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.opsAllReady}</p>
        ) : (
          rows.map((row) => (
            <div key={row.reason} className="space-y-1">
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span>{t.opsReason[row.reason]}</span>
                <span className="tnum font-semibold">
                  {formatCount(row.rooms, locale)}
                </span>
              </div>
              {/* The bar is a share of the not-ready rooms, not of the hotel: the panel's
                  question is which of these to act on first. */}
              <Progress
                value={row.rooms}
                max={total}
                label={t.opsReason[row.reason]}
                tone={
                  row.reason === 'OUT_OF_ORDER' || row.reason === 'OUT_OF_SERVICE'
                    ? 'var(--hk-sv-ooo-text)'
                    : 'var(--hk-primary)'
                }
              />
            </div>
          ))
        )}
      </CardBody>
    </Card>
  )
}

/**
 * **EVERY LANE IN THE HOTEL**, where M-12 shows one supervisor's floors. Same cards, same
 * builder, same substitution rule — `LaneCards` decides all of it, and this draws it.
 */
function FloorsPanel({ lanes }: { lanes: SupervisorLane[] }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  return (
    <Card>
      <CardHeader title={t.opsFloors} />
      <CardBody className="space-y-3">
        {lanes.length === 0 ? (
          <div className="space-y-1">
            <p className="text-sm font-medium">{t.opsNobodyHolding}</p>
            <p className="text-sm text-muted-foreground">{t.opsNobodyHoldingBody}</p>
          </div>
        ) : (
          lanes.map((lane) => (
            <LaneRow key={lane.teamId ?? lane.userId} lane={lane} />
          ))
        )}
      </CardBody>
    </Card>
  )
}

function LaneRow({ lane }: { lane: SupervisorLane }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  /* The workload is the sum of exactly the cards he is holding — the same arithmetic the
     assignment board does, over the same array, because the server sends the cards rather
     than a total for precisely this reason. */
  const load = lane.tasks.reduce((sum, task) => sum + task.credits, 0)

  const notes = [
    `${t.opsLaneDone} ${formatCount(lane.doneCount, locale)}`,
    lane.runningRoomNumber
      ? fill(t.opsLaneIn, { room: lane.runningRoomNumber })
      : null,
    lane.reworkCount > 0
      ? fill(t.opsLaneRework, { count: formatCount(lane.reworkCount, locale) })
      : null,
  ].filter(Boolean) as string[]

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        {/* His name, in his own script, isolated — a Latin name in an Arabic line and an
            Arabic name in a Latin one both need the same protection. */}
        <span dir="auto" style={{ unicodeBidi: 'isolate' }} className="truncate font-medium">
          {nameIn(locale, lane.displayName, lane.displayNameAr)}
        </span>
        <span className="tnum shrink-0 text-xs text-muted-foreground">
          {/* **NO BUDGET IS NOT A BUDGET OF ZERO.** A man holding rooms with nothing on
              today's roster is a fact a manager needs to see, and a "0 of 0" would hide
              it behind arithmetic. */}
          {lane.creditBudget === null
            ? t.opsLaneNoBudget
            : fill(t.opsLaneLoad, {
                credits: formatCredits(load, locale),
                budget: formatCredits(lane.creditBudget, locale),
              })}
        </span>
      </div>

      {lane.creditBudget !== null && lane.creditBudget > 0 && (
        <Progress
          value={load}
          max={lane.creditBudget}
          label={lane.displayName}
          tone={load > lane.creditBudget ? 'var(--hk-sv-ooo-text)' : 'var(--hk-primary)'}
        />
      )}

      <p className="text-xs text-muted-foreground">{notes.join(' · ')}</p>
    </div>
  )
}
